import { WarningOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';

import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import {
  type CUSTOMER_QUERY_DATE_RANGE,
  CUSTOMER_QUERY_DOMAIN,
  type CUSTOMER_QUERY_SENTIMENT,
} from '@/types/customerConst';

import { SENTIMENT_CHART_OFFSET } from './ChartLabels';
import { CustomerSentimentTrendChart } from './CustomerSentimentTrendChart';
import CustomerTableStyles from './CustomerTableStyles';
import { SentimentTrendLineChart } from './SentimentTrendLineChart';
const { StyledCol, StyledRow, StyledSentimentTrendChartWrapper } =
  CustomerTableStyles;

type CustomerRowProps = {
  domain: CUSTOMER_QUERY_DOMAIN;
  startDate: Dayjs;
  endDate: Dayjs;
  dateRange: CUSTOMER_QUERY_DATE_RANGE;
  sentiment?: CUSTOMER_QUERY_SENTIMENT;
  sentimentTrendChartMode: 'dot' | 'line';
  hideNeutral: boolean;
} & CustomerSentimentContactItem;

const CustomerRow = (props: CustomerRowProps) => {
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const color = useColor();

  const customerQueryModalDic = {
    [CUSTOMER_QUERY_DOMAIN.CONTACT]: 'customer',
    [CUSTOMER_QUERY_DOMAIN.ACCOUNT]: 'company',
  };

  return (
    <StyledRow>
      <StyledCol
        className="col-name"
        style={{ cursor: 'pointer' }}
        onClick={() => {
          setNewModal({
            alias: props.alias,
            type: customerQueryModalDic[props.domain] as DETAIL_MODAL_TYPE,
            idx: 0,
          });
        }}
      >
        <VFlex g={4} padding="10px 18px">
          <Typo
            type="label2"
            className="text-gray-800"
            weight="sb"
            ellipsis
            style={{
              maxWidth: '160px',
            }}
          >
            {props.name}
          </Typo>

          <Typo
            type="label2"
            className="text-gray-700"
            weight="r"
            ellipsis
            style={{
              maxWidth: '160px',
            }}
          >
            {props.email}
          </Typo>
        </VFlex>
      </StyledCol>
      <StyledCol className="col-negative">
        {props.negative_sentiment_count ? (
          <Button
            leftIcon={<WarningOutlined />}
            sizeType="m"
            variant="error_outlined"
            rounded
          >
            {props.negative_sentiment_count}(
            {(props.negative_sentiment_ratio * 100).toFixed(0)}%)
          </Button>
        ) : (
          <Flex a="center">
            <Space w={40} />
            <Typo type="label1" className="text-gray-600">
              -
            </Typo>
          </Flex>
        )}
      </StyledCol>
      <StyledCol className="col-trend body-row">
        <StyledSentimentTrendChartWrapper>
          {props.sentimentTrendChartMode === 'dot' ? (
            <CustomerSentimentTrendChart
              startDate={props.startDate}
              endDate={props.endDate}
              onChartSentenceClick={(alias) =>
                setNewModal({
                  alias,
                  type: DETAIL_MODAL_TYPE.FEEDBACK,
                  idx: 0,
                })
              }
              data={props.sentiments.filter((sentiment) => {
                if (props.hideNeutral) {
                  return sentiment.value !== 2;
                }
                return true;
              })}
              offset={SENTIMENT_CHART_OFFSET}
              radius={14}
            />
          ) : (
            <SentimentTrendLineChart
              sentiment={props.sentiment}
              data={props.sentiments}
              startDate={props.startDate}
              endDate={props.endDate}
              dateRange={props.dateRange}
              chartStyle={{
                width: '100%',
                height: '100%',
              }}
            />
          )}
        </StyledSentimentTrendChartWrapper>
      </StyledCol>
      <StyledCol
        className="col-sentence"
        style={{ cursor: 'pointer' }}
        onClick={() => {
          setNewModal({
            alias: props.recent_feedback.alias,
            type: DETAIL_MODAL_TYPE.FEEDBACK,
            idx: 0,
          });
        }}
      >
        <Space w={16} />
        <Flex a="flex-start" g={8} f={1}>
          <div
            style={{
              transform: 'translateY(6px)',
              backgroundColor: color.sentiment[props.recent_feedback.sentiment],
              width: 8,
              height: 8,
              borderRadius: 4,
            }}
          ></div>
          <Typo type="body2" multilineEllipsis={3} style={{ flex: 1 }}>
            {props.recent_feedback.text}
          </Typo>
        </Flex>
      </StyledCol>
    </StyledRow>
  );
};

export default CustomerRow;
